import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Badge } from './badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './dialog';
import { Package, Minus, Plus, AlertTriangle } from 'lucide-react';

/**
 * Standardized quantity popup for adding articles/consumables.
 * Supports decimal input, min/max bounds, stock info display.
 *
 * Props:
 *   article       - { name, code, unit, id, total_stock, min_stock, ... } or null
 *   onConfirm     - (article, qty) => {} called with article + chosen quantity
 *   onCancel      - () => {} called when dialog closed
 *   title         - Dialog title (default: "Adauga Consumabil")
 *   minQty        - Minimum quantity (default: 0.01)
 *   maxQty        - Maximum quantity (default: null = no limit)
 *   step          - Step for stepper buttons (default: 1)
 *   initialQty    - Initial quantity (default: 1)
 *   showStock     - Show stock info badge (default: true)
 *   confirmLabel  - Button label (default: "Adauga")
 */
const ArticleQuantityDialog = ({
  article, onConfirm, onCancel,
  title = 'Adauga Consumabil',
  minQty = 0.01, maxQty = null, step = 1,
  initialQty = 1, showStock = true, confirmLabel = 'Adauga'
}) => {
  const [qty, setQty] = useState(initialQty);

  useEffect(() => {
    if (article) setQty(initialQty);
  }, [article, initialQty]);

  if (!article) return null;

  const effectiveMax = maxQty ?? (article.total_stock > 0 ? article.total_stock : 99999);
  const isOverMax = maxQty !== null && qty > maxQty;
  const isValid = qty >= minQty && !isOverMax;

  const handleInputChange = (e) => {
    const val = e.target.value;
    if (val === '' || val === '.') { setQty(val); return; }
    const num = parseFloat(val);
    if (!isNaN(num) && num >= 0) setQty(num);
  };

  const handleBlur = () => {
    const num = parseFloat(qty);
    if (isNaN(num) || num < minQty) setQty(minQty);
  };

  return (
    <Dialog open={!!article} onOpenChange={(open) => { if (!open) onCancel?.(); }}>
      <DialogContent className="max-w-sm" data-testid="article-quantity-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Package className="w-5 h-5 text-blue-500" />
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="font-bold text-slate-900 text-sm">{article.name}</p>
            <p className="text-xs text-slate-500 font-mono mt-0.5">{article.code}</p>
            {showStock && article.total_stock !== undefined && (
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="text-[10px]">
                  Stoc: {article.total_stock} {article.unit || 'buc'}
                </Badge>
                {article.min_stock > 0 && (
                  <Badge variant={article.total_stock <= article.min_stock ? 'destructive' : 'outline'} className="text-[10px]">
                    Min: {article.min_stock}
                  </Badge>
                )}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label className="font-semibold text-xs">Cantitate</Label>
            <div className="flex items-center gap-2 justify-center">
              <button
                className="w-11 h-11 rounded-xl border-2 border-slate-200 bg-white flex items-center justify-center text-slate-700 hover:bg-slate-50 active:bg-slate-200 transition-colors"
                onClick={() => setQty(Math.max(minQty, (parseFloat(qty) || 0) - step))}
                data-testid="article-qty-minus"
              >
                <Minus className="w-4 h-4" />
              </button>
              <Input
                type="number"
                value={qty}
                onChange={handleInputChange}
                onBlur={handleBlur}
                min={minQty}
                max={maxQty || undefined}
                step={step < 1 ? '0.01' : '1'}
                className="w-24 h-11 text-center text-lg font-bold border-2 border-blue-300 bg-blue-50 text-blue-700 rounded-xl [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                data-testid="article-qty-value"
              />
              <button
                className="w-11 h-11 rounded-xl border-2 border-slate-200 bg-white flex items-center justify-center text-slate-700 hover:bg-slate-50 active:bg-slate-200 transition-colors"
                onClick={() => setQty(Math.min(effectiveMax, (parseFloat(qty) || 0) + step))}
                data-testid="article-qty-plus"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <p className="text-center text-[10px] text-slate-500">{article.unit || 'buc'}</p>
            {isOverMax && (
              <p className="text-center text-xs text-red-600 flex items-center justify-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Maxim: {maxQty}
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onCancel?.()} data-testid="cancel-article-qty-btn">Anuleaza</Button>
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
            disabled={!isValid}
            onClick={() => onConfirm?.(article, parseFloat(qty) || minQty)}
            data-testid="confirm-article-qty-btn"
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { ArticleQuantityDialog };
export default ArticleQuantityDialog;
